import React, { useMemo, useState } from 'react';
import { Card, Col, Drawer, Input, message, Modal, Row, Select, Space, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { LeftOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import { useGetMatLogisticPackages, useGetMatLogisticPackageShipmentEventsByPackageId } from '../../../hooks/genericHooks';
import { deleteData } from '../../../services/db.api';
import type { MatLogisticPackage, MatLogisticPackageShipmentEvent } from '../../../types';
import { Tooltip } from 'antd';
import { CgAttachment } from 'react-icons/cg';
import { GoHistory } from 'react-icons/go';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const RCgAttachment: React.FC<any> = (props) =>
  React.createElement(CgAttachment as unknown as React.ComponentType<any>, props) as React.ReactElement;

const MatLogPackageList: React.FC = () => {
  const navigate = useNavigate();
  const { data = [], isLoading, refetch } = useGetMatLogisticPackages() as any;
  const packages: MatLogisticPackage[] = Array.isArray(data) ? data : (data?.data ?? []);

  const [eventsDrawerOpen, setEventsDrawerOpen] = useState(false);
  const [eventsPackageId, setEventsPackageId] = useState<string | null>(null);
  const { data: eventsDataRaw, isLoading: eventsLoading } = useGetMatLogisticPackageShipmentEventsByPackageId(eventsPackageId) as any;
  const packageEvents: MatLogisticPackageShipmentEvent[] = Array.isArray(eventsDataRaw)
    ? eventsDataRaw
    : (eventsDataRaw?.data ?? []);

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<{
    status?: string;
    transport_type?: string;
    package_type?: string;
    shipment_id?: string;
    q?: string;
  }>({});
  const [deleting, setDeleting] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<MatLogisticPackage[]>([]);

  const norm = (v?: any) => (v === null || v === undefined ? '' : String(v)).trim();

  const filteredData = useMemo(() => {
    const q = (filters.q || '').trim().toLowerCase();
    return (packages || []).filter((r) => {
      if (filters.status && norm(r.status) !== norm(filters.status)) return false;
      if (filters.transport_type && norm(r.transport_type) !== norm(filters.transport_type)) return false;
      if (filters.package_type && norm(r.package_type) !== norm(filters.package_type)) return false;
      if (filters.shipment_id && norm(r.shipment_id) !== norm(filters.shipment_id)) return false;

      if (q) {
        const hay = [
          r.po_number,
          r.package_id,
          r.shipment_id,
          r.previous_shipment_id,
          r.transport_type,
          r.package_type,
          r.status,
          r.final_destination,
          r.remarks,
        ]
          .map((x) => (x == null ? '' : String(x)))
          .join(' ')
          .toLowerCase();

        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [filters, packages]);

  const makeOptions = <K extends keyof MatLogisticPackage>(key: K) => {
    const set = new Set(
      (packages || [])
        .map((r) => r[key])
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '') as any
    );
    return Array.from(set)
      .sort((a: any, b: any) => String(a).localeCompare(String(b)))
      .map((v: any) => ({ label: String(v), value: String(v) }));
  };

  const columns: ColumnType<MatLogisticPackage>[] = useMemo(
    () => [
      { title: 'Package ID', dataIndex: 'package_id', key: 'package_id', width: 140 },
      {
        title: 'Att',
        key: 'has_files',
        width: 36,
        align: 'center' as const,
        onHeaderCell: () => ({ style: { width: 36, minWidth: 36, maxWidth: 36 } }),
        onCell: () => ({ style: { width: 36, minWidth: 36, maxWidth: 36 } }),
        render: (_: any, row: MatLogisticPackage) => {
          const has = !!(row as any)?.upload_path && String((row as any).upload_path).trim() !== '';
          return has ? (
            <Tooltip title="Has attachments">
              <RCgAttachment size={16} color="#1677ff" />
            </Tooltip>
          ) : null;
        },
      },
      { title: 'PO', dataIndex: 'po_number', key: 'po_number', width: 120 },
      { title: 'Shipment', dataIndex: 'shipment_id', key: 'shipment_id', width: 140 },
      { title: 'Transport', dataIndex: 'transport_type', key: 'transport_type', width: 100 },
      { title: 'Type', dataIndex: 'package_type', key: 'package_type', width: 110 },
      { title: 'Status', dataIndex: 'status', key: 'status', width: 110 },
      {
        title: 'Gross (kg)',
        dataIndex: 'gross_weight_kg',
        key: 'gross_weight_kg',
        width: 110,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v: any) => {
          const n = typeof v === 'number' ? v : Number(String(v));
          return Number.isFinite(n) ? n.toFixed(3) : '';
        },
      },
      {
        title: 'Freight Ton',
        dataIndex: 'freight_ton',
        key: 'freight_ton',
        width: 110,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v: any) => {
          const n = typeof v === 'number' ? v : Number(String(v));
          return Number.isFinite(n) ? n.toFixed(3) : '';
        },
      },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 115,
        render: (v) => (v ? String(v).slice(0, 10) : '-'),
        exportValue: (r: MatLogisticPackage) => (r.updated_at ? dayjs(r.updated_at).format('YYYY-MM-DD') : ''),
      },
      createActionColumn<MatLogisticPackage>({
        width: 120,
        showView: false,
        extraRender: (r) => (
          <Tooltip title="Events">
            <Button
              size="small"
              icon={<GoHistory style={{ fontSize: 14 }} />}
              style={{ backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}
              onClick={() => {
                const pid = String((r as any)?.package_id || '').trim();
                if (!pid) {
                  message.error('Missing package_id; cannot load events.');
                  return;
                }
                setEventsPackageId(pid);
                setEventsDrawerOpen(true);
              }}
              aria-label="Events"
            />
          </Tooltip>
        ),
        onEdit: (r) => navigate(`/material/logistics/packages/edit/${encodeURIComponent(String(r.id))}`),
        onDelete: (r) => {
          const pid = String((r as any)?.package_id || '').trim();
          if (!pid) {
            message.error('Missing package_id on this row; cannot delete safely.');
            return;
          }
          Modal.confirm({
            title: 'Delete package?',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            content: `Package: ${pid}. This will also delete all package items under this package.`,
            onOk: async () => {
              if (deleting) return;
              setDeleting(true);
              try {
                // FK is often RESTRICT; delete items first, then header.
                // NOTE: /dbExchange/delete_where is allowlisted on backend; use generic /dbExchange/delete.
                await deleteData('mat_logistic_package_items', 'package_id', pid);
                await deleteData('mat_logistic_packages', 'package_id', pid);
                message.success(`Deleted package ${pid}`);
                await refetch?.();
              } catch (e: any) {
                message.error(e?.message || `Failed to delete package ${pid}`);
              } finally {
                setDeleting(false);
              }
            },
          });
        },
      }),
    ],
    [deleting, navigate, refetch]
  );

  const eventColumns: ColumnType<MatLogisticPackageShipmentEvent>[] = useMemo(
    () => [
      { title: 'Event', dataIndex: 'event_type', key: 'event_type', width: 120 },
      { title: 'Shipment', dataIndex: 'shipment_id', key: 'shipment_id', width: 140 },
      { title: 'From', dataIndex: 'from_shipment_id', key: 'from_shipment_id', width: 140 },
      { title: 'To', dataIndex: 'to_shipment_id', key: 'to_shipment_id', width: 140 },
      {
        title: 'When',
        dataIndex: 'event_at',
        key: 'event_at',
        width: 115,
        render: (v) => (v ? String(v).slice(0, 19).replace('T', ' ') : '-'),
        exportValue: (r: MatLogisticPackageShipmentEvent) => (r.event_at ? dayjs(r.event_at).format('YYYY-MM-DD HH:mm') : ''),
      },
      { title: 'By', dataIndex: 'event_by', key: 'event_by', width: 110 },
      { title: 'Note', dataIndex: 'note', key: 'note', width: 220 },
    ],
    []
  );

  const selectedPackageIds = useMemo(() => {
    const ids = (selectedRows || [])
      .map((r) => String((r as any)?.package_id || '').trim())
      .filter(Boolean);
    // de-dup
    return Array.from(new Set(ids));
  }, [selectedRows]);

  const selectedPackageDbIds = useMemo(() => {
    const ids = (selectedRows || [])
      .map((r) => (r as any)?.id)
      .filter((v) => v !== null && v !== undefined)
      .map((v) => String(v).trim())
      .filter((v) => v);
    return Array.from(new Set(ids));
  }, [selectedRows]);

  const handleDeleteSelected = () => {
    const ids = selectedPackageIds;
    if (!ids.length) {
      message.info('No rows selected');
      return;
    }
    Modal.confirm({
      title: 'Delete selected packages?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      content: `This will delete ${ids.length} package(s) and all their items.`,
      onOk: async () => {
        if (deleting) return;
        setDeleting(true);
        const successes: string[] = [];
        const failures: Array<{ pid: string; message: string }> = [];
        try {
          for (const pid of ids) {
            try {
              // FK is often RESTRICT; delete items first, then header.
              await deleteData('mat_logistic_package_items', 'package_id', pid);
              await deleteData('mat_logistic_packages', 'package_id', pid);
              successes.push(pid);
            } catch (e: any) {
              failures.push({ pid, message: e?.message || 'Unknown error' });
            }
          }

          if (successes.length) {
            message.success(`Deleted ${successes.length} package(s)`);
          }
          if (failures.length) {
            Modal.warning({
              title: 'Some deletions failed',
              content: (
                <div>
                  <div style={{ marginBottom: 8 }}>
                    Deleted {successes.length}. Failed {failures.length}.
                  </div>
                  <div style={{ maxHeight: 240, overflow: 'auto' }}>
                    {failures.map((f) => (
                      <div key={f.pid} style={{ marginBottom: 6 }}>
                        <b>{f.pid}</b>: {f.message}
                      </div>
                    ))}
                  </div>
                </div>
              ),
            });
          }
        } finally {
          setDeleting(false);
          setSelectedRowKeys([]);
          setSelectedRows([]);
          await refetch?.();
        }
      },
    });
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} />
            <Title level={4} style={{ margin: 0 }}>
              Logistics Packages {filteredData?.length ? `(${filteredData.length})` : ''}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button action="add" icon={<PlusOutlined />} onClick={() => navigate('/material/logistics/packages/create')}>
              Create Package
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch?.()}>
              Refresh
            </Button>
            <Button
              action="edit"
              disabled={selectedPackageDbIds.length === 0}
              onClick={() => {
                if (selectedPackageDbIds.length === 1) {
                  navigate(`/material/logistics/packages/edit/${encodeURIComponent(selectedPackageDbIds[0])}`);
                  return;
                }
                const qs = new URLSearchParams();
                qs.set('ids', selectedPackageDbIds.join(','));
                navigate(`/material/logistics/packages/edit-multi?${qs.toString()}`);
              }}
            >
              Edit Selected{selectedPackageDbIds.length ? ` (${selectedPackageDbIds.length})` : ''}
            </Button>
            <Button action="delete" danger disabled={deleting || selectedPackageIds.length === 0} onClick={handleDeleteSelected}>
              Delete Selected{selectedPackageIds.length ? ` (${selectedPackageIds.length})` : ''}
            </Button>
            <Button onClick={() => setFilters({})}>Clear Filters</Button>
            <Button onClick={() => setShowFilters((v) => !v)}>{showFilters ? 'Hide Filters' : 'Show Filters'}</Button>
          </Space>
        </Col>
      </Row>

      {showFilters && (
        <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Status"
              style={{ width: 140 }}
              value={filters.status}
              options={makeOptions('status')}
              onChange={(v) => setFilters((p) => ({ ...p, status: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Transport"
              style={{ width: 140 }}
              value={filters.transport_type}
              options={makeOptions('transport_type')}
              onChange={(v) => setFilters((p) => ({ ...p, transport_type: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Package Type"
              style={{ width: 160 }}
              value={filters.package_type}
              options={makeOptions('package_type')}
              onChange={(v) => setFilters((p) => ({ ...p, package_type: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Shipment"
              style={{ width: 160 }}
              value={filters.shipment_id}
              options={makeOptions('shipment_id')}
              onChange={(v) => setFilters((p) => ({ ...p, shipment_id: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Input
              allowClear
              placeholder="Search package/shipment/type/status/destination..."
              style={{ width: 320 }}
              value={filters.q}
              onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value || undefined }))}
            />
          </Col>
        </Row>
      )}

      <StandardTable<MatLogisticPackage>
        columns={columns}
        dataSource={filteredData}
        rowKey={(r) => String(r.package_id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows as MatLogisticPackage[]);
          },
        }}
        enableExport
        enableCellTooltips
        scroll={{ x: 'max-content' }}
        exportFileName="mat_logistic_packages"
      />

      <Drawer
        open={eventsDrawerOpen}
        width={960}
        title={eventsPackageId ? `Package Events: ${eventsPackageId}` : 'Package Events'}
        onClose={() => setEventsDrawerOpen(false)}
        destroyOnClose
      >
        <StandardTable<MatLogisticPackageShipmentEvent>
          columns={eventColumns}
          dataSource={packageEvents}
          rowKey={(r) => String(r.id)}
          loading={eventsLoading}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          enableExport
          enableCellTooltips
          scroll={{ x: 'max-content' }}
          exportFileName={`mat_logistic_package_shipment_events_${eventsPackageId || 'package'}`}
        />
      </Drawer>
    </Card>
  );
};

export default MatLogPackageList;
